import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function parseNorNumber(str) {
    if (!str || !str.trim()) return null;
    const s = str.trim();
    if (/[eE]/.test(s)) return parseFloat(s.replace(',', '.'));
    // Strip both dots (old format thousands) and spaces (new format thousands), then swap decimal comma
    return parseFloat(s.replace(/[\s.]/g, '').replace(',', '.'));
}

function parseCsvText(text) {
    const content = text.replace(/^﻿/, '').replace(/^ï»¿/, '');
    const lines = content.split(/\r?\n/);
    if (lines.length < 2) return [];

    const headerCols = lines[0].split(';').map(h => h.trim());
    const hasVaremerke = headerCols[2] === 'Varemerke';
    const hasModell    = hasVaremerke && headerCols[3] === 'Modell';
    const iVaremerke = hasVaremerke ? 2 : -1;
    const iFarge     = hasVaremerke ? (hasModell ? 4 : 3) : 2;
    const iStorrelse = hasVaremerke ? (hasModell ? 5 : 4) : 3;
    const iArtikkel  = hasVaremerke ? (hasModell ? 6 : 5) : 4;
    const iGTIN      = hasVaremerke ? (hasModell ? 7 : 6) : 5;
    const iSolgt     = hasVaremerke ? (hasModell ? 8 : 7) : 6;
    const iLager     = hasVaremerke ? (hasModell ? 9 : 8) : 7;
    const iBrutto    = hasVaremerke ? (hasModell ? 13 : 12) : 11;
    const iKostpris  = hasVaremerke ? (hasModell ? 17 : 16) : 15;

    const items = [];
    for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        const cols = line.split(';');
        if (cols.length < 6) continue;

        const varetekst = cols[1]?.trim();
        if (!varetekst) continue;

        const butikk = (cols[0]?.trim() || '').replace(/\s*\(\d+\)\s*$/, '').trim();

        const brandRaw = iVaremerke !== -1 ? cols[iVaremerke]?.trim() : '';
        const brand = brandRaw && brandRaw !== '-' ? brandRaw : '';

        const varenrRaw = cols[iArtikkel]?.trim();
        const varenr = varenrRaw && varenrRaw !== '-' ? varenrRaw : '';

        const gtinRaw = cols[iGTIN]?.trim();
        const ean = gtinRaw && gtinRaw !== '-' ? gtinRaw : '';

        const solgtAntall = parseNorNumber(cols[iSolgt]);
        const bruttosalg = parseNorNumber(cols[iBrutto]);
        const price = (solgtAntall > 0 && bruttosalg > 0)
            ? Math.round(bruttosalg / solgtAntall)
            : null;

        const item = {
            butikk,
            product: varetekst,
            farge: cols[iFarge]?.trim() || '',
            størrelse: cols[iStorrelse]?.trim() || '',
            varenr,
            ean,
            lagerantall: parseNorNumber(cols[iLager]),
            kostpris: parseNorNumber(cols[iKostpris]),
        };
        if (brand) item.brand = brand;
        if (price != null) item.price = price;
        items.push(item);
    }

    return items;
}

function parseSdvText(text) {
    // col[0]=Butikk, col[1]=EAN/PLU, col[4]=Fabrikat, col[5]=Varetekst, col[8]=Lagerantall, col[10]=Kostpris (total), col[11]=Ordinær pris
    const content = text.replace(/^﻿/, '').replace(/^ï»¿/, '');
    const lines = content.split(/\r?\n/);
    if (lines.length < 2) return [];

    const seen = new Set();
    const items = [];
    for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        const cols = line.split(';');
        if (cols.length < 6) continue;

        const butikk = (cols[0]?.trim() || '').replace(/\s*\(\d+\)\s*$/, '').trim();
        const brand = cols[4]?.trim() || '';
        const product = cols[5]?.trim() || '';
        if (!product) continue;

        const key = `${butikk}|${brand}|${product}`;
        if (seen.has(key)) continue;
        seen.add(key);

        // EAN/PLU from col[1] — may be stored as scientific notation by Excel
        const eanRaw = cols[1]?.trim() || '';
        let ean = '';
        if (eanRaw) {
            if (/[eE]/.test(eanRaw)) {
                const num = parseFloat(eanRaw.replace(',', '.'));
                if (isFinite(num) && num > 0) ean = Math.round(num).toString();
            } else {
                ean = eanRaw.replace(/[^0-9]/g, '');
            }
        }

        const lagerantall = parseNorNumber(cols[8]);
        const totalKostpris = parseNorNumber(cols[10]);
        const kostpris = (totalKostpris != null && lagerantall != null && lagerantall > 0)
            ? Math.round((totalKostpris / lagerantall) * 100) / 100
            : null;
        const price = parseNorNumber(cols[11]);

        const item = { butikk, product };
        if (brand) item.brand = brand;
        if (ean) item.ean = ean;
        if (lagerantall != null) item.lagerantall = lagerantall;
        if (kostpris != null) item.kostpris = kostpris;
        if (price != null) item.price = price;
        items.push(item);
    }
    return items;
}

function fmtDate(d) {
    if (!d) return '–';
    return new Date(d).toLocaleDateString('no-NO', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtNum(n) {
    if (n == null) return '–';
    return n.toLocaleString('no-NO');
}

export default function PartList() {
    const [parts, setParts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    const [importState, setImportState] = useState(null); // null | 'preview' | 'importing' | 'done'
    const [importItems, setImportItems] = useState([]);
    const [importResult, setImportResult] = useState(null);
    const [importError, setImportError] = useState('');
    const [importVaregruppe, setImportVaregruppe] = useState('');

    const [priceImportState, setPriceImportState] = useState(null);
    const [priceItems, setPriceItems] = useState([]);
    const [priceImportResult, setPriceImportResult] = useState(null);
    const [priceImportError, setPriceImportError] = useState('');

    const [deletingAll, setDeletingAll] = useState(false);

    const fileRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetch(`${API_BASE_URL}/parts`)
            .then(r => r.json())
            .then(data => { setParts(data); setLoading(false); })
            .catch(() => setLoading(false));
    }, []);

    function handleFileChange(e) {
        const file = e.target.files[0];
        if (!file) return;
        e.target.value = '';

        const reader = new FileReader();
        reader.onload = (ev) => {
            try {
                const text = ev.target.result;
                const firstLine = text.split(/\r?\n/)[0];
                const headerCols = firstLine.split(';').map(h => h.trim());

                if (headerCols[1] === 'EAN/PLU') {
                    const items = parseSdvText(text);
                    if (items.length === 0) {
                        setImportError('Fant ingen varer i filen. Sjekk at filen er riktig format.');
                        setImportState('done');
                        return;
                    }
                    setImportItems(items);
                    setImportError('');
                    setImportState('preview');
                } else {
                    const items = parseCsvText(text);
                    if (items.length === 0) {
                        setImportError('Fant ingen varer i filen. Sjekk at filen er riktig format (semikolon-separert).');
                        setImportState('done');
                        return;
                    }
                    setImportItems(items);
                    setImportError('');
                    setImportState('preview');
                }
            } catch {
                setImportError('Kunne ikke lese filen.');
                setImportState('done');
            }
        };
        reader.readAsText(file, 'windows-1252');
    }

    async function doImport() {
        setImportState('importing');
        try {
            const r = await fetch(`${API_BASE_URL}/parts/import`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items: importItems, varegruppe: importVaregruppe }),
            });
            const data = await r.json();
            setImportResult(data);
            const updated = await fetch(`${API_BASE_URL}/parts`).then(x => x.json());
            setParts(updated);
        } catch {
            setImportError('Nettverksfeil under import.');
        }
        setImportState('done');
    }

    async function doPriceImport() {
        setPriceImportState('importing');
        try {
            const r = await fetch(`${API_BASE_URL}/parts/import-prices`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items: priceItems }),
            });
            const data = await r.json();
            setPriceImportResult(data);
            const updated = await fetch(`${API_BASE_URL}/parts`).then(x => x.json());
            setParts(updated);
        } catch {
            setPriceImportError('Nettverksfeil under import.');
        }
        setPriceImportState('done');
    }

    function closePriceImport() {
        setPriceImportState(null);
        setPriceItems([]);
        setPriceImportResult(null);
        setPriceImportError('');
    }

    async function deleteAllParts() {
        if (!window.confirm(`Slette alle ${parts.length} varer fra varelisten? Dette kan ikke angres.`)) return;
        setDeletingAll(true);
        try {
            await fetch(`${API_BASE_URL}/parts`, { method: 'DELETE' });
            setParts([]);
        } catch {
            alert('Nettverksfeil ved sletting.');
        }
        setDeletingAll(false);
    }

    function closeImport() {
        setImportState(null);
        setImportItems([]);
        setImportResult(null);
        setImportError('');
        setImportVaregruppe('');
    }

    const filtered = parts.filter(p => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return (
            p.product?.toLowerCase().includes(q) ||
            p.brand?.toLowerCase().includes(q) ||
            p.ean?.includes(q) ||
            p.farge?.toLowerCase().includes(q) ||
            p.størrelse?.toLowerCase().includes(q)
        );
    });

    return (
        <div className="max-w-6xl mx-auto py-8 bg-white shadow-lg rounded-lg p-6 mb-4">
            <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
                <h2 className="text-2xl font-semibold">Vareliste</h2>
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={() => fileRef.current?.click()}
                        className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded text-sm font-medium transition"
                    >
                        Importer CSV
                    </button>
                    <button
                        onClick={() => navigate('/part-form')}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm font-medium transition"
                    >
                        Legg til vare
                    </button>
                    <button
                        onClick={deleteAllParts}
                        disabled={deletingAll || parts.length === 0}
                        className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-4 py-2 rounded text-sm font-medium transition"
                    >
                        {deletingAll ? 'Sletter…' : 'Slett alle varer'}
                    </button>
                </div>
                <input
                    ref={fileRef}
                    type="file"
                    accept=".csv,.sdv,.txt"
                    className="hidden"
                    onChange={handleFileChange}
                />
            </div>

            <input
                type="text"
                placeholder="Søk i varemerke, produkt, EAN..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-teal-500"
            />

            {loading ? (
                <p className="text-sm text-gray-500">Laster vareliste…</p>
            ) : filtered.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                    {search ? 'Ingen varer matcher søket.' : 'Ingen varer lagt til ennå.'}
                </p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm border border-gray-200 rounded">
                        <thead>
                            <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                                <th className="px-4 py-2 border-b w-10"></th>
                                <th className="px-4 py-2 border-b">Varemerke</th>
                                <th className="px-4 py-2 border-b">Varetekst</th>
                                <th className="px-4 py-2 border-b hidden lg:table-cell">EAN</th>
                                <th className="px-4 py-2 border-b text-right">Lager</th>
                                <th className="px-4 py-2 border-b text-right hidden sm:table-cell">Kostpris</th>
                                <th className="px-4 py-2 border-b text-right">Pris</th>
                                <th className="px-4 py-2 border-b hidden lg:table-cell">Oppdatert</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(p => (
                                <tr
                                    key={p._id}
                                    className="hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                                    onClick={() => navigate(`/part-change/${p._id}`)}
                                >
                                    <td className="px-2 py-1">
                                        {p.imageUrl ? (
                                            <img
                                                src={p.imageUrl}
                                                alt=""
                                                className="w-9 h-9 object-contain rounded bg-gray-50 border"
                                            />
                                        ) : (
                                            <div className="w-9 h-9 rounded bg-gray-100 border flex items-center justify-center text-gray-300 text-xs">
                                                –
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-2 font-medium">{p.brand || '–'}</td>
                                    <td className="px-4 py-2">
                                        <div>{p.product || '–'}</div>
                                        {(p.farge || p.størrelse) && (
                                            <div className="text-xs text-gray-400">{[p.farge, p.størrelse].filter(Boolean).join(' / ')}</div>
                                        )}
                                    </td>
                                    <td className="px-4 py-2 text-gray-400 text-xs hidden lg:table-cell">{p.ean || '–'}</td>
                                    <td className="px-4 py-2 text-right">
                                        <span className={`font-medium ${p.lagerantall > 0 ? 'text-green-700' : 'text-gray-400'}`}>
                                            {p.lagerantall ?? '–'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2 text-right hidden sm:table-cell text-gray-500">
                                        {p.kostpris != null ? `${fmtNum(p.kostpris)} kr` : '–'}
                                    </td>
                                    <td className="px-4 py-2 text-right font-medium">
                                        {p.price != null ? `${fmtNum(p.price)} kr` : '–'}
                                    </td>
                                    <td className="px-4 py-2 text-gray-400 text-xs hidden lg:table-cell">
                                        {fmtDate(p.sisteOppdatert)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <p className="text-xs text-gray-400 mt-2">{filtered.length} varer vist</p>
                </div>
            )}

            {priceImportState && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
                        {priceImportState === 'preview' && (
                            <>
                                <h3 className="text-lg font-semibold mb-1">Importer utpris</h3>
                                <p className="text-sm text-gray-600 mb-4">
                                    Fant <strong>{priceItems.length}</strong> varer med utpris. Matcher på butikk + varemerke + varetekst og oppdaterer prisfeltet.
                                </p>
                                <div className="overflow-x-auto max-h-48 border rounded mb-4">
                                    <table className="text-xs w-full">
                                        <thead className="bg-gray-50 sticky top-0">
                                            <tr>
                                                <th className="px-2 py-1 text-left border-b">Butikk</th>
                                                <th className="px-2 py-1 text-left border-b">Varemerke</th>
                                                <th className="px-2 py-1 text-left border-b">Varetekst</th>
                                                <th className="px-2 py-1 text-right border-b">Utpris</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {priceItems.slice(0, 8).map((item, i) => (
                                                <tr key={i} className="border-b last:border-0">
                                                    <td className="px-2 py-1 text-gray-500">{item.butikk || '–'}</td>
                                                    <td className="px-2 py-1 text-gray-500">{item.brand || '–'}</td>
                                                    <td className="px-2 py-1">{item.product}</td>
                                                    <td className="px-2 py-1 text-right font-medium">{item.price} kr</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <p className="text-xs text-gray-500 mb-4">
                                    Kun prisfeltet oppdateres. Andre feltene røres ikke.
                                </p>
                                <div className="flex gap-2 justify-end">
                                    <button onClick={closePriceImport} className="px-4 py-2 border rounded text-sm hover:bg-gray-50">
                                        Avbryt
                                    </button>
                                    <button onClick={doPriceImport} className="px-4 py-2 bg-teal-600 text-white rounded text-sm hover:bg-teal-700">
                                        Oppdater {priceItems.length} varer
                                    </button>
                                </div>
                            </>
                        )}

                        {priceImportState === 'importing' && (
                            <div className="text-center py-6">
                                <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                                <p className="text-sm text-gray-600">Oppdaterer priser…</p>
                            </div>
                        )}

                        {priceImportState === 'done' && (
                            <>
                                <h3 className="text-lg font-semibold mb-3">Import fullført</h3>
                                {priceImportResult && (
                                    <div className="space-y-1 mb-4 text-sm">
                                        <p className="text-blue-700">↻ {priceImportResult.updated} varer oppdatert med pris</p>
                                    </div>
                                )}
                                {priceImportError && (
                                    <p className="text-sm text-red-600 mb-4">{priceImportError}</p>
                                )}
                                <div className="flex justify-end">
                                    <button onClick={closePriceImport} className="px-4 py-2 bg-teal-600 text-white rounded text-sm hover:bg-teal-700">
                                        Lukk
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {importState && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
                        {importState === 'preview' && (
                            <>
                                <h3 className="text-lg font-semibold mb-1">Importer vareliste</h3>
                                <p className="text-sm text-gray-600 mb-3">
                                    Fant <strong>{importItems.length}</strong> varer i filen. Butikk hentes automatisk fra filen.
                                </p>
                                <div className="mb-4">
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Varegruppe <span className="text-gray-400 font-normal">(valgfri)</span></label>
                                    <select
                                        value={importVaregruppe}
                                        onChange={e => setImportVaregruppe(e.target.value)}
                                        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                    >
                                        <option value="">— Velg varegruppe —</option>
                                        <option>SPORTSUTSTYR OG TILBEHØR</option>
                                        <option>FRITIDSBEKLEDNING</option>
                                        <option>FOTTØY</option>
                                        <option>SYKKEL</option>
                                        <option>VINTERSPORT</option>
                                        <option>JAKT - FISKE - VÅPEN</option>
                                        <option>FRILUFTSUTSTYR</option>
                                        <option>VANNSPORT</option>
                                        <option>EGNE GRUPPER</option>
                                    </select>
                                </div>
                                <div className="overflow-x-auto max-h-48 border rounded mb-4">
                                    <table className="text-xs w-full">
                                        <thead className="bg-gray-50 sticky top-0">
                                            <tr>
                                                <th className="px-2 py-1 text-left border-b">Butikk</th>
                                                {importItems.some(it => it.brand) && (
                                                    <th className="px-2 py-1 text-left border-b">Varemerke</th>
                                                )}
                                                <th className="px-2 py-1 text-left border-b">Varetekst</th>
                                                <th className="px-2 py-1 text-left border-b">Farge/Str</th>
                                                <th className="px-2 py-1 text-right border-b">Lager</th>
                                                <th className="px-2 py-1 text-right border-b">Kostpris</th>
                                                <th className="px-2 py-1 text-right border-b">Pris*</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {importItems.slice(0, 8).map((item, i) => (
                                                <tr key={i} className="border-b last:border-0">
                                                    <td className="px-2 py-1 text-gray-500">{item.butikk || '–'}</td>
                                                    {importItems.some(it => it.brand) && (
                                                        <td className="px-2 py-1 text-gray-500">{item.brand || '–'}</td>
                                                    )}
                                                    <td className="px-2 py-1">{item.product}</td>
                                                    <td className="px-2 py-1 text-gray-500">{[item.farge, item.størrelse].filter(Boolean).join(' / ') || '–'}</td>
                                                    <td className="px-2 py-1 text-right">{item.lagerantall ?? '–'}</td>
                                                    <td className="px-2 py-1 text-right">{item.kostpris != null ? `${item.kostpris} kr` : '–'}</td>
                                                    <td className="px-2 py-1 text-right">{item.price != null ? `${item.price} kr` : '–'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <p className="text-xs text-gray-400 mb-1">* Pris er beregnet fra Bruttosalg ÷ Solgt antall (gjennomsnittlig salgspris).</p>
                                <p className="text-xs text-gray-500 mb-4">
                                    Eksisterende varer oppdateres (basert på butikk + EAN/varenr). Nye varer opprettes.
                                </p>
                                <div className="flex gap-2 justify-end">
                                    <button onClick={closeImport} className="px-4 py-2 border rounded text-sm hover:bg-gray-50">
                                        Avbryt
                                    </button>
                                    <button onClick={doImport} className="px-4 py-2 bg-teal-600 text-white rounded text-sm hover:bg-teal-700">
                                        Importer {importItems.length} varer
                                    </button>
                                </div>
                            </>
                        )}

                        {importState === 'importing' && (
                            <div className="text-center py-6">
                                <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                                <p className="text-sm text-gray-600">Importerer {importItems.length} varer…</p>
                            </div>
                        )}

                        {importState === 'done' && (
                            <>
                                <h3 className="text-lg font-semibold mb-3">Import fullført</h3>
                                {importResult && (
                                    <div className="space-y-1 mb-4 text-sm">
                                        <p className="text-green-700">✓ {importResult.created} nye varer opprettet</p>
                                        <p className="text-blue-700">↻ {importResult.updated} varer oppdatert</p>
                                        {importResult.errors > 0 && (
                                            <p className="text-red-600">✗ {importResult.errors} feil</p>
                                        )}
                                    </div>
                                )}
                                {importError && (
                                    <p className="text-sm text-red-600 mb-4">{importError}</p>
                                )}
                                <div className="flex justify-end">
                                    <button onClick={closeImport} className="px-4 py-2 bg-teal-600 text-white rounded text-sm hover:bg-teal-700">
                                        Lukk
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
